<template>
  <el-dialog
    v-model="dialogVisible"
    v-bind="$attrs"
    align-center
    append-to-body
    :body-class="'dialog-layout__body'"
    class="dialog-layout"
    :class="{
      'dialog-layout--content-border': showContentBorder,
      'dialog-layout--no-header': !isShowHeader && !showClose
    }"
    :close-on-click-modal="closeOnClickModal"
    :destroy-on-close="destroyOnClose"
    :footer-class="'dialog-layout__footer'"
    :header-class="'dialog-layout__header'"
    :show-close="showClose"
    :style="dialogStyle"
    :width="dialogWidth"
  >
    <template v-if="isShowHeader" #header>
      <slot name="header">
        <div class="dialog-layout__header-content">
          <div class="dialog-layout__title">{{ title }}</div>
        </div>
      </slot>
    </template>

    <slot></slot>
    <!-- <div class="dialog-layout__content">
    </div> -->

    <template v-if="$slots.footer" #footer>
      <slot name="footer"></slot>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, useSlots } from 'vue'

defineOptions({
  name: 'DialogLayout',
  inheritAttrs: false
})

const emits = defineEmits(['update:show'])
const slots = useSlots()

const props = defineProps({
  // 对话框显示状态，配合 v-model:show 使用
  show: {
    type: Boolean,
    default: false
  },
  // 默认标题文案，可通过 header 插槽覆盖
  title: {
    type: String,
    default: ''
  },
  // 弹窗内容区域内边距
  bodyPadding: {
    type: [Number, String],
    default: '0 24px'
  },
  // 是否显示内容区域上下分割线
  showContentBorder: {
    type: Boolean,
    default: false
  },
  // 是否显示右上角关闭按钮
  showClose: {
    type: Boolean,
    default: true
  },
  // 是否允许点击遮罩关闭弹窗
  closeOnClickModal: {
    type: Boolean,
    default: false
  },
  // 关闭时是否销毁弹窗内容
  destroyOnClose: {
    type: Boolean,
    default: true
  },
  // 弹窗固定宽度，传入后优先级高于 minWidth / maxWidth / horizontalGap
  width: {
    type: [Number, String],
    default: ''
  },
  // 弹窗固定高度，传入后优先级高于 minHeight / maxHeight / verticalGap
  height: {
    type: [Number, String],
    // default: ''
    default: 'auto'
  },
  // 弹窗距离页面左右两侧的安全间距
  horizontalGap: {
    type: [Number, String],
    default: 216
  },
  // 弹窗距离页面上下两侧的安全间距
  verticalGap: {
    type: [Number, String],
    default: 80
  },
  // 弹窗最小宽度
  minWidth: {
    type: [Number, String],
    default: ''
  },
  // 弹窗最大宽度
  maxWidth: {
    type: [Number, String],
    default: ''
  },
  // 弹窗最小高度
  minHeight: {
    type: [Number, String],
    default: ''
  },
  // 弹窗最大高度
  maxHeight: {
    type: [Number, String],
    default: ''
  }
})

const formatUnit = (value) => {
  if (typeof value === 'number') {
    return `${value}px`
  }

  return value
}

const dialogVisible = computed({
  get: () => props.show,
  set: (value) => emits('update:show', value)
})

const isShowHeader = computed(() => {
  return !!props.title || !!slots.header
})

const hasCustomWidth = computed(() => {
  //   return props.width !== '' && props.width !== null && props.width !== undefined
  return !!props.width
})

const hasCustomHeight = computed(() => {
  // return props.height !== '' && props.height !== null && props.height !== undefined
  // 当没有设置[最大高度、最小高度]，标识走自定义的height，内容自适应height
  return !!props.height && !(props.minHeight || props.maxHeight)
})

const dialogWidth = computed(() => {
  if (hasCustomWidth.value) {
    return formatUnit(props.width)
  }

  return `calc(100vw - ${formatUnit(props.horizontalGap)} * 2)`
})

const dialogMaxHeight = computed(() => {
  const viewportMaxHeight = `calc(100vh - ${formatUnit(props.verticalGap)} * 2)`

  if (!props.maxHeight) {
    return viewportMaxHeight
  }

  return `min(${formatUnit(props.maxHeight)}, ${viewportMaxHeight})`
})

const dialogStyle = computed(() => ({
  '--dialog-layout-body-padding': formatUnit(props.bodyPadding),
  ...(hasCustomWidth.value
    ? {}
    : {
        minWidth: formatUnit(props.minWidth),
        maxWidth: formatUnit(props.maxWidth)
      }),
  ...(hasCustomHeight.value
    ? {
        height: formatUnit(props.height)
      }
    : {
        minHeight: formatUnit(props.minHeight),
        maxHeight: dialogMaxHeight.value
      })
}))

const open = () => {
  dialogVisible.value = true
}

const close = () => {
  dialogVisible.value = false
}

defineExpose({
  open,
  close
})
</script>

<style lang="scss">
$dialog-layout-bg: #ffffff; // 填充/纯白
$dialog-layout-title: #323233; // 文字/Text-4 强调正文标题
$dialog-layout-close-icon: #646566; // 文字/Text-3 次强调正文标题
$dialog-layout-close-bg: #f7f8fa; // 填充/Fill-1 浅色 禁用背景
$dialog-layout-close-bg-hover: #f2f3f5; // 填充/Fill-2 一般 常规 白底悬浮
$dialog-layout-shadow: 0 2px 15px 2px rgba(0, 0, 0, 0.05); // 阴影

.dialog-layout {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  overflow: hidden;
  border: none;
  border-radius: 24px !important;
  background: $dialog-layout-bg;
  box-shadow: $dialog-layout-shadow;

  .el-dialog__headerbtn {
    top: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0;
    border-radius: 8px;
    background: $dialog-layout-close-bg;
    color: $dialog-layout-close-icon;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    .el-dialog__close {
      font-size: 16px;
      color: inherit;
    }
  }

  .el-dialog__headerbtn:hover {
    background: $dialog-layout-close-bg-hover;
    color: $dialog-layout-title;
  }

  .dialog-layout__header {
    padding: 24px;
    border-bottom: none;
    background: $dialog-layout-bg;
  }

  .dialog-layout__header-content {
    display: flex;
    align-items: center;
    padding-right: 40px;
  }

  .dialog-layout__title {
    height: 26px;
    line-height: 26px;
    font-size: 18px;
    font-weight: 500;
    color: #323233;
  }

  .dialog-layout__body {
    padding: var(--dialog-layout-body-padding);
    flex-grow: 1;
    overflow: auto;
    background: $dialog-layout-bg;
    box-sizing: border-box;
  }

  .dialog-layout__content {
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: auto;
  }

  .dialog-layout__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 20px 24px;
    border-top: none;
    background: $dialog-layout-bg;
    box-sizing: border-box;
  }

  &.dialog-layout--no-header .dialog-layout__header {
    display: none;
  }

  &.dialog-layout--content-border {
    .dialog-layout__header {
      border-bottom: 1px solid #f2f3f5;
    }

    .dialog-layout__footer {
      border-top: 1px solid #f2f3f5;
    }
  }
}
</style>
